/**
 * Numerology study — full course structure (single source of truth).
 * Routes: /numerology/study/module-1-foundations … module-7-full-reading
 */

export const NUMEROLOGY_COURSE = [
  {
    id: 'm1',
    path: 'module-1-foundations',
    number: 1,
    title: 'Foundations',
    outcome: 'Understand the system, reduction, and how a reading is structured.',
    description:
      'What numerology measures, how numbers are produced, core positions in outline, reduction, master numbers, and the order of a reading.',
    lessons: [
      { slug: 'm1-what-is-numerology', label: 'What is numerology' },
      { slug: 'm1-how-numerology-works', label: 'How numerology works' },
      { slug: 'your-core-numbers', label: 'Core numbers overview' },
      { slug: 'm1-reduction-rules', label: 'Reduction rules' },
      { slug: 'master-numbers-11-22-33', label: 'Master numbers' },
      { slug: 'm1-structure-of-a-reading', label: 'How a numerology reading is structured' },
    ],
  },
  {
    id: 'm2',
    path: 'module-2-core-calculations',
    number: 2,
    title: 'Core numbers',
    outcome: 'Calculate Life Path, name numbers, and birth day from date and name.',
    description:
      'Life Path, master Life Paths, birthday, and the full birth name as one system — plus optional deep dives on each name angle.',
    lessons: [
      { slug: 'life-path-number-calculator', label: 'Life Path number calculation' },
      { slug: 'm2-master-life-paths', label: 'Master Life Paths 11, 22, 33' },
      { slug: 'your-birth-day-number', label: 'Birthday number' },
      { slug: 'your-name-numerology', label: 'Name numerology (full name)' },
    ],
    optionalLessons: [
      { slug: 'm2-expression-number', label: 'Expression number (optional deep dive)' },
      { slug: 'm2-soul-urge-number', label: 'Soul Urge number (optional deep dive)' },
      { slug: 'm2-personality-number', label: 'Personality number (optional deep dive)' },
    ],
  },
  {
    id: 'm3',
    path: 'module-3-number-meanings',
    number: 3,
    title: 'Number meanings',
    outcome:
      'Use 1–9 as reading language: strengths, shadow, dominant vs quiet digits, how they interact across positions, and how that shows up in written reports.',
    description:
      'The shared digit language behind every chart — how 1–9 behave in readings, where they strengthen, distort, repeat, go quiet, and meet each other across positions — and how those patterns read in a full report.',
    lessons: [
      {
        slug: 'numerology-numbers-1-9',
        label: 'Digits 1–9: meanings, shadow, and interaction',
      },
    ],
  },
  {
    id: 'm4',
    path: 'module-4-secondary-numbers',
    number: 4,
    title: 'Secondary numbers',
    outcome: 'Add depth with maturity, attitude, passion, karmic markers, and balance.',
    description:
      'Optional layers that sharpen a chart once core numbers and digit language are in place.',
    lessons: [
      { slug: 'm3-maturity-number', label: 'Maturity number' },
      { slug: 'm3-attitude-number', label: 'Attitude number' },
      { slug: 'm3-hidden-passion', label: 'Hidden Passion number' },
      { slug: 'm3-karmic-debt', label: 'Karmic Debt numbers' },
      { slug: 'm3-karmic-lessons', label: 'Karmic Lessons' },
      { slug: 'm3-balance-number', label: 'Balance number' },
    ],
  },
  {
    id: 'm5',
    path: 'module-5-personal-timing',
    number: 5,
    title: 'Combining numbers & timing',
    outcome:
      'See how numbers interact in a chart, then read Personal Year, Month, and Day as layers on top.',
    description:
      'Contradictions, dominant and repeating patterns, missing energies, and how major name numbers meet Life Path — then seasonal timing (year, month, day) and how to interpret the stack.',
    lessons: [
      { slug: 'm5-contradictions', label: 'Contradicting numbers' },
      { slug: 'm5-dominant-patterns', label: 'Dominant patterns' },
      { slug: 'm5-repeating-numbers', label: 'Repeating numbers' },
      { slug: 'm5-missing-energies', label: 'Missing energies' },
      { slug: 'm5-life-path-expression', label: 'Life Path + Expression' },
      { slug: 'm5-soul-urge-vs-personality', label: 'Soul Urge vs Personality' },
      { slug: 'm4-personal-year', label: 'Personal Year number' },
      { slug: 'm4-personal-month', label: 'Personal Month' },
      { slug: 'm4-personal-day', label: 'Personal Day' },
      { slug: 'numerology-timing-cycles', label: 'Personal Year, Month & Day (combined)' },
      { slug: 'm4-interpret-timing', label: 'How to interpret timing periods' },
    ],
  },
  {
    id: 'm6',
    path: 'module-6-life-cycles',
    number: 6,
    title: 'Reading examples & life cycles',
    outcome: 'Study worked readings and long-arc timing — life cycles, pinnacles, and challenges.',
    description:
      'Example readings and a full profile walkthrough, then longer timing structures — chapters of life and friction points.',
    lessons: [
      { slug: 'm6-example-reading-1', label: 'Example reading 1 — Edgar Allan Poe (study sample)' },
      { slug: 'm6-example-reading-2', label: 'Example reading 2 — Marie Curie (study sample)' },
      { slug: 'm6-example-reading-3', label: 'Example reading 3 — Composite chart (teaching walk-through)' },
      { slug: 'm6-read-profile-start-to-finish', label: 'How to read a profile from start to finish' },
      { slug: 'm4-life-cycles', label: 'Life Cycles' },
      { slug: 'm4-pinnacles', label: 'Pinnacles' },
      { slug: 'm4-challenges', label: 'Challenges' },
    ],
  },
  {
    id: 'm7',
    path: 'module-7-full-reading',
    number: 7,
    title: 'Full reading',
    outcome: 'Read charts with confidence, then study samples and a capstone framework.',
    description:
      'Chart-reading skills, sample reports, the full framework, and a worked example — synthesis and integration in one module.',
    lessons: [
      { slug: 'how-to-read-your-numerology-chart', label: 'How to read a chart' },
      { slug: 'm6-step-by-step-breakdown', label: 'Step-by-step reading breakdown' },
      { slug: 'm7-combine-major-numbers', label: 'How to combine all major numbers' },
      { slug: 'm7-how-to-write-interpretations', label: 'How to write interpretations' },
      { slug: 'm7-structure-complete-reading', label: 'How to structure a complete reading' },
      { type: 'route', to: '/numerology/study/samples', label: 'Sample readings' },
      { slug: 'm7-full-reading-framework', label: 'Full reading framework' },
      { slug: 'm7-final-example', label: 'Final full reading example' },
      { slug: 'your-personal-numerology-report', label: 'Your personal report' },
    ],
  },
];

/** Optional appendix — not part of the numbered linear path (only items listed here appear on the course landing page) */
export const NUMEROLOGY_COURSE_APPENDIX = [
  { slug: 'appendix-quick-reference', label: 'Quick reference guide' },
];

/** Current module paths (for module hub pages) */
export const MODULE_PATHS = new Set(NUMEROLOGY_COURSE.map((m) => m.path));

/**
 * Old module paths → canonical path (same content, new pedagogy order).
 * Apply before rendering module hub pages.
 */
export const MODULE_PATH_LEGACY_REDIRECTS = {
  'module-2-core-numbers': 'module-2-core-calculations',
  'module-3-secondary-numbers': 'module-4-secondary-numbers',
  'module-4-timing-cycles': 'module-5-personal-timing',
  'module-5-combining-numbers': 'module-5-personal-timing',
  'module-6-example-readings': 'module-6-life-cycles',
  'module-7-full-reading-method': 'module-7-full-reading',
};

export function getModuleByPath(path) {
  return NUMEROLOGY_COURSE.find((m) => m.path === path) ?? null;
}

export function getAdjacentModules(path) {
  const i = NUMEROLOGY_COURSE.findIndex((m) => m.path === path);
  if (i < 0) return { prev: null, next: null };
  return {
    prev: i > 0 ? NUMEROLOGY_COURSE[i - 1] : null,
    next: i < NUMEROLOGY_COURSE.length - 1 ? NUMEROLOGY_COURSE[i + 1] : null,
  };
}

/** First lesson slug in a module (for “Start module”) */
export function getModuleStartSlug(module) {
  const first = module?.lessons?.find((l) => l.slug);
  return first?.slug ?? null;
}

/**
 * Linear course order (modules → lessons), including route steps (e.g. sample readings).
 * Used for prev/next on /numerology/study/:slug lesson pages. Route steps are in the chain
 * for hrefs but only slug-based pages resolve `slug` to an index.
 */
export function getCourseLessonAdjacency(slug) {
  if (!slug) return null;
  const flat = [];
  for (const mod of NUMEROLOGY_COURSE) {
    for (const lesson of mod.lessons) {
      if (lesson.type === 'route') {
        flat.push({
          module: mod,
          label: lesson.label,
          href: lesson.to,
          slug: null,
        });
      } else {
        flat.push({
          module: mod,
          slug: lesson.slug,
          label: lesson.label,
          href: `/numerology/study/${lesson.slug}`,
        });
      }
    }
  }
  const idx = flat.findIndex((e) => e.slug === slug);
  if (idx < 0) return null;
  const prev = idx > 0 ? flat[idx - 1] : null;
  const next = idx < flat.length - 1 ? flat[idx + 1] : null;
  return { prev, next, module: flat[idx].module };
}

/** @deprecated Use NUMEROLOGY_COURSE */
export const NUMEROLOGY_COURSE_MODULES = NUMEROLOGY_COURSE.map((m) => ({
  id: m.id,
  title: `Module ${m.number} — ${m.title}`,
  description: m.description,
  outcome: m.outcome,
  path: m.path,
  items: [
    ...m.lessons.map((l) =>
      l.type === 'route' ? { type: 'route', to: l.to, label: l.label } : { slug: l.slug, label: l.label }
    ),
    ...(m.optionalLessons || []).map((l) => ({ slug: l.slug, label: l.label, optional: true })),
  ],
}));
